import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, PencilSimple, UserPlus, Pause, Play, MagnifyingGlass, FunnelSimple } from '@phosphor-icons/react';

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState({ publishers: [] });
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showApplicantsDialog, setShowApplicantsDialog] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [publisherEmail, setPublisherEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [tableStatusFilter, setTableStatusFilter] = useState('all');
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    category: '',
    target_markets: '',
    assigned_publishers: '',
    assigned_brand: '',
    content_type: '',
    content_budget: '',
    distribution_budget: '',
    commerce_links: '',
    start_date: '',
    end_date: '',
    description: '',
    earning_model: ''
  });
  const [campaignImage, setCampaignImage] = useState(null);
  const [showAeoSection, setShowAeoSection] = useState(false);
  const [aeoForm, setAeoForm] = useState({
    primary_query: '',
    secondary_queries: '',
    search_intent_type: '',
    content_structure_type: '',
    brand_placement_type: '',
    key_brand_narratives: '',
    priority_use_cases: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, usersRes, benchmarksRes, publishersRes, brandsRes] = await Promise.all([
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/roi-benchmarks`),
        axios.get(`${API}/admin/publishers-list`),
        axios.get(`${API}/admin/brands-list`)
      ]);
      setCampaigns(campaignsRes.data);
      setBenchmarks(benchmarksRes.data);
      
      // Extract publishers for easy selection
      const publishersList = publishersRes.data.map(p => ({
        id: p.user.id,
        email: p.user.email,
        name: p.profile?.name || p.user.email,
        company: p.user.company_name
      }));
      
      // Extract brands for easy selection
      const brandsList = brandsRes.data.map(b => ({
        id: b.user.id,
        email: b.user.email,
        name: b.profile?.company_name || b.user.email
      }));
      
      setUsers({ publishers: publishersList, brands: brandsList, all: usersRes.data });
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...campaignForm,
        target_markets: campaignForm.target_markets.split(',').map(c => c.trim()),
        assigned_publishers: campaignForm.assigned_publishers.split(',').map(c => c.trim()).filter(c => c),
        commerce_links: campaignForm.commerce_links.split(',').map(c => c.trim()).filter(c => c),
        content_budget: parseFloat(campaignForm.content_budget),
        distribution_budget: parseFloat(campaignForm.distribution_budget),
        description: campaignForm.description || null,
        earning_model: campaignForm.earning_model || null,
        aeo_brief: showAeoSection && aeoForm.primary_query ? {
          primary_query: aeoForm.primary_query,
          secondary_queries: aeoForm.secondary_queries.split('\n').map(q => q.trim()).filter(Boolean),
          search_intent_type: aeoForm.search_intent_type || null,
          content_structure_type: aeoForm.content_structure_type || null,
          brand_placement_type: aeoForm.brand_placement_type || null,
          key_brand_narratives: aeoForm.key_brand_narratives || null,
          priority_use_cases: aeoForm.priority_use_cases || null
        } : null
      };

      const res = await axios.post(`${API}/campaigns`, payload);

      // Upload image if selected
      if (campaignImage && res.data?.id) {
        const formData = new FormData();
        formData.append('file', campaignImage);
        await axios.post(`${API}/campaigns/${res.data.id}/image`, formData);
      }

      toast.success('Campaign created successfully!');
      setShowCreateDialog(false);
      setCampaignForm({
        name: '',
        category: '',
        target_markets: '',
        assigned_publishers: '',
        assigned_brand: '',
        content_type: '',
        content_budget: '',
        distribution_budget: '',
        commerce_links: '',
        start_date: '',
        end_date: '',
        description: '',
        earning_model: ''
      });
      setCampaignImage(null);
      setShowAeoSection(false);
      setAeoForm({ primary_query: '', secondary_queries: '', search_intent_type: '', content_structure_type: '', brand_placement_type: '', key_brand_narratives: '', priority_use_cases: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (campaignId, newStatus) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}`, { status: newStatus });
      toast.success('Campaign status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      category: campaign.category,
      target_markets: campaign.target_markets.join(', '),
      assigned_publishers: campaign.assigned_publishers.join(', '),
      assigned_brand: campaign.assigned_brand,
      content_type: campaign.content_type,
      content_budget: campaign.content_budget.toString(),
      distribution_budget: campaign.distribution_budget.toString(),
      commerce_links: campaign.commerce_links.join(', '),
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateCampaign = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        name: campaignForm.name,
        category: campaignForm.category,
        target_markets: campaignForm.target_markets.split(',').map(c => c.trim()),
        assigned_publishers: campaignForm.assigned_publishers.split(',').map(c => c.trim()).filter(c => c),
        assigned_brand: campaignForm.assigned_brand,
        content_type: campaignForm.content_type,
        content_budget: parseFloat(campaignForm.content_budget),
        distribution_budget: parseFloat(campaignForm.distribution_budget),
        commerce_links: campaignForm.commerce_links.split(',').map(c => c.trim()).filter(c => c),
        start_date: campaignForm.start_date || null,
        end_date: campaignForm.end_date || null
      };

      await axios.put(`${API}/campaigns/${selectedCampaign.id}`, payload);
      toast.success('Campaign updated successfully!');
      setShowEditDialog(false);
      setSelectedCampaign(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignPublisher = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.put(
        `${API}/campaigns/${selectedCampaign.id}/assign-publisher`,
        null,
        { params: { publisher_email: publisherEmail } }
      );
      toast.success(`Publisher ${publisherEmail} assigned successfully!`);
      setShowAssignDialog(false);
      setPublisherEmail('');
      setSelectedCampaign(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign publisher');
    } finally {
      setSubmitting(false);
    }
  };

  const openAssignDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setShowAssignDialog(true);
  };

  const openApplicantsDialog = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const res = await axios.get(`${API}/admin/campaigns/${campaign.id}/applications`);
      setApplicants(res.data);
    } catch {
      setApplicants([]);
    }
    setShowApplicantsDialog(true);
  };

  const handleApproveApplicant = async (appId) => {
    try {
      await axios.put(`${API}/admin/campaigns/${selectedCampaign.id}/applications/${appId}/approve`);
      toast.success('Publisher approved and assigned!');
      setApplicants(prev => prev.map(a => a.id === appId ? { ...a, status: 'approved' } : a));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectApplicant = async (appId) => {
    try {
      await axios.put(`${API}/admin/campaigns/${selectedCampaign.id}/applications/${appId}/reject`);
      toast.success('Application rejected');
      setApplicants(prev => prev.map(a => a.id === appId ? { ...a, status: 'rejected' } : a));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-accent border-foreground';
      case 'completed': return 'bg-muted border-foreground';
      case 'paused': return 'bg-background border-foreground';
      case 'draft': return 'bg-background border-foreground';
      default: return 'border-foreground';
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="p-8">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="p-8" data-testid="admin-campaigns-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-heading text-4xl font-bold">Campaign Management</h1>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-campaign-button">
                <Plus size={20} className="mr-2" weight="bold" />
                Create Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Campaign</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCampaign} className="space-y-4" data-testid="campaign-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Campaign Name</Label>
                    <Input
                      id="name"
                      value={campaignForm.name}
                      onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={campaignForm.category}
                      onValueChange={(value) => setCampaignForm({ ...campaignForm, category: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {benchmarks.map(b => (
                          <SelectItem key={b.category} value={b.category}>{b.category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="assigned_brand">Assigned Brand</Label>
                  <Select
                    value={campaignForm.assigned_brand}
                    onValueChange={(value) => setCampaignForm({ ...campaignForm, assigned_brand: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.brands && users.brands.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} ({b.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="target_markets">Target Markets (comma-separated)</Label>
                  <Input
                    id="target_markets"
                    value={campaignForm.target_markets}
                    onChange={(e) => setCampaignForm({ ...campaignForm, target_markets: e.target.value })}
                    placeholder="US, UK, India"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="assigned_publishers">Initial Publishers (optional - can assign later)</Label>
                  <Input
                    id="assigned_publishers"
                    value={campaignForm.assigned_publishers}
                    onChange={(e) => setCampaignForm({ ...campaignForm, assigned_publishers: e.target.value })}
                    placeholder="Leave empty to assign later using 'Assign Publisher' button"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Better to create campaign first, then use "Assign Publisher" button
                  </p>
                </div>

                <div>
                  <Label htmlFor="content_type">Content Type</Label>
                  <Input
                    id="content_type"
                    value={campaignForm.content_type}
                    onChange={(e) => setCampaignForm({ ...campaignForm, content_type: e.target.value })}
                    placeholder="Blog Post, Video, Sponsored Article"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="content_budget">Content Budget ($)</Label>
                    <Input
                      id="content_budget"
                      type="number"
                      value={campaignForm.content_budget}
                      onChange={(e) => setCampaignForm({ ...campaignForm, content_budget: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="distribution_budget">Distribution Budget ($)</Label>
                    <Input
                      id="distribution_budget"
                      type="number"
                      value={campaignForm.distribution_budget}
                      onChange={(e) => setCampaignForm({ ...campaignForm, distribution_budget: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={campaignForm.start_date}
                      onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={campaignForm.end_date}
                      onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="commerce_links">Commerce Links (comma-separated)</Label>
                  <Input
                    id="commerce_links"
                    value={campaignForm.commerce_links}
                    onChange={(e) => setCampaignForm({ ...campaignForm, commerce_links: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    value={campaignForm.description}
                    onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                    placeholder="Brief description of the campaign"
                    rows={2}
                    className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="earning_model">Earning Model</Label>
                    <Input
                      id="earning_model"
                      value={campaignForm.earning_model}
                      onChange={(e) => setCampaignForm({ ...campaignForm, earning_model: e.target.value })}
                      placeholder="e.g. CPS — 10% or Fixed Fee — ₹5,000"
                      className="mt-1"
                      data-testid="earning-model-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="campaign_image">Campaign Image (optional)</Label>
                    <Input
                      id="campaign_image"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => setCampaignImage(e.target.files?.[0] || null)}
                      className="mt-1"
                      data-testid="campaign-image-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">JPG or PNG only</p>
                  </div>
                </div>

                {/* AEO Optimisation Section */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowAeoSection(!showAeoSection)}
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
                    data-testid="aeo-toggle"
                  >
                    <span>Add AEO Optimisation (optional)</span>
                    <span className="text-xs text-muted-foreground">{showAeoSection ? '▲' : '▼'}</span>
                  </button>
                  {showAeoSection && (
                    <div className="border-t border-border p-4 space-y-4 bg-muted/20" data-testid="aeo-section">
                      <div>
                        <Label>Primary Query</Label>
                        <Input
                          value={aeoForm.primary_query}
                          onChange={(e) => setAeoForm({ ...aeoForm, primary_query: e.target.value })}
                          placeholder="e.g. best running shoes under 5000"
                          className="mt-1"
                          data-testid="aeo-primary-query"
                        />
                      </div>
                      <div>
                        <Label>Secondary Queries</Label>
                        <textarea
                          value={aeoForm.secondary_queries}
                          onChange={(e) => setAeoForm({ ...aeoForm, secondary_queries: e.target.value })}
                          placeholder="Add one query per line"
                          rows={3}
                          className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          data-testid="aeo-secondary-queries"
                        />
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Search Intent Type</Label>
                          <Select value={aeoForm.search_intent_type} onValueChange={(v) => setAeoForm({ ...aeoForm, search_intent_type: v })}>
                            <SelectTrigger className="mt-1" data-testid="aeo-search-intent">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {['Best', 'Comparison', 'Review', 'How-to', 'Guide'].map(v => (
                                <SelectItem key={v} value={v}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Content Structure Type</Label>
                          <Select value={aeoForm.content_structure_type} onValueChange={(v) => setAeoForm({ ...aeoForm, content_structure_type: v })}>
                            <SelectTrigger className="mt-1" data-testid="aeo-content-structure">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {['Listicle', 'Comparison Table', 'FAQ', 'Guide'].map(v => (
                                <SelectItem key={v} value={v}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Brand Placement Type</Label>
                          <Select value={aeoForm.brand_placement_type} onValueChange={(v) => setAeoForm({ ...aeoForm, brand_placement_type: v })}>
                            <SelectTrigger className="mt-1" data-testid="aeo-brand-placement">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {['Primary Recommendation', 'Secondary Inclusion', 'Contextual Mention'].map(v => (
                                <SelectItem key={v} value={v}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Key Brand Narratives</Label>
                        <textarea
                          value={aeoForm.key_brand_narratives}
                          onChange={(e) => setAeoForm({ ...aeoForm, key_brand_narratives: e.target.value })}
                          placeholder="e.g. most affordable, best for beginners"
                          rows={2}
                          className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          data-testid="aeo-brand-narratives"
                        />
                      </div>
                      <div>
                        <Label>Priority Use Cases</Label>
                        <textarea
                          value={aeoForm.priority_use_cases}
                          onChange={(e) => setAeoForm({ ...aeoForm, priority_use_cases: e.target.value })}
                          placeholder="e.g. best for daily use, budget option"
                          rows={2}
                          className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          data-testid="aeo-use-cases"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground">
                    {submitting ? 'Creating...' : 'Create Campaign'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Campaign Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Campaign</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateCampaign} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_name">Campaign Name</Label>
                  <Input
                    id="edit_name"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_category">Category</Label>
                  <Input
                    id="edit_category"
                    value={campaignForm.category}
                    onChange={(e) => setCampaignForm({ ...campaignForm, category: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit_target_markets">Target Markets (comma-separated)</Label>
                <Input
                  id="edit_target_markets"
                  value={campaignForm.target_markets}
                  onChange={(e) => setCampaignForm({ ...campaignForm, target_markets: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="edit_content_type">Content Type</Label>
                <Input
                  id="edit_content_type"
                  value={campaignForm.content_type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, content_type: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_content_budget">Content Budget ($)</Label>
                  <Input
                    id="edit_content_budget"
                    type="number"
                    value={campaignForm.content_budget}
                    onChange={(e) => setCampaignForm({ ...campaignForm, content_budget: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_distribution_budget">Distribution Budget ($)</Label>
                  <Input
                    id="edit_distribution_budget"
                    type="number"
                    value={campaignForm.distribution_budget}
                    onChange={(e) => setCampaignForm({ ...campaignForm, distribution_budget: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground">
                  {submitting ? 'Updating...' : 'Update Campaign'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Publisher Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Publisher to Campaign</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAssignPublisher} className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-semibold mb-2">Campaign:</p>
                <p className="text-sm">{selectedCampaign?.name}</p>
              </div>

              <div>
                <Label htmlFor="publisher_email">Select Publisher</Label>
                <Select
                  value={publisherEmail}
                  onValueChange={setPublisherEmail}
                >
                  <SelectTrigger className="mt-1" data-testid="publisher-select">
                    <SelectValue placeholder="Choose a publisher..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.publishers && users.publishers.length > 0 ? (
                      users.publishers.map((pub) => (
                        <SelectItem key={pub.id} value={pub.email} data-testid={`publisher-option-${pub.email}`}>
                          <div className="flex flex-col">
                            <span className="font-medium">{pub.name}</span>
                            <span className="text-xs text-muted-foreground">{pub.email}</span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>No publishers available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Select from approved publishers on the platform
                </p>
              </div>

              {selectedCampaign?.assigned_publishers && selectedCampaign.assigned_publishers.length > 0 && (
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Currently Assigned:</p>
                  <div className="space-y-1">
                    {selectedCampaign.assigned_publishers.map((pubId) => {
                      const publisher = users.publishers?.find(p => p.id === pubId);
                      return publisher ? (
                        <div key={pubId} className="text-sm flex justify-between items-center">
                          <span>{publisher.email}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button type="submit" disabled={submitting || !publisherEmail} className="bg-primary text-primary-foreground">
                  {submitting ? 'Assigning...' : 'Assign Publisher'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {campaigns.length === 0 ? (
          <div className="rounded-xl border border-[var(--adm-border)] bg-[var(--adm-surface)] p-12 text-center">
            <p className="text-[var(--adm-muted)]">No campaigns yet. Create your first campaign.</p>
          </div>
        ) : (
          <>
            {/* Search & Filter Bar */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input
                  type="text"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Search campaigns..."
                  className="h-9 w-full rounded-lg border border-[#E5E7EB] bg-white pl-9 pr-3 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-[#DC120F]/40 focus:ring-1 focus:ring-[#DC120F]/20"
                  data-testid="campaign-table-search"
                />
              </div>
              <div className="relative">
                <FunnelSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <select
                  value={tableStatusFilter}
                  onChange={(e) => setTableStatusFilter(e.target.value)}
                  className="h-9 appearance-none rounded-lg border border-[#E5E7EB] bg-white pl-8 pr-8 text-sm text-[#374151] outline-none focus:border-[#DC120F]/40"
                  data-testid="campaign-status-filter"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-[#E5E7EB]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA]">
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Campaign Name</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Category</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Status</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Budget</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Est. ROI</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-[#6B7280]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns
                      .filter(c => {
                        const matchesSearch = !tableSearch || c.name.toLowerCase().includes(tableSearch.toLowerCase()) || c.category.toLowerCase().includes(tableSearch.toLowerCase());
                        const matchesStatus = tableStatusFilter === 'all' || c.status === tableStatusFilter;
                        return matchesSearch && matchesStatus;
                      })
                      .map((campaign) => {
                        const dotColor = campaign.status === 'active' ? 'bg-emerald-500' : campaign.status === 'paused' ? 'bg-amber-400' : campaign.status === 'completed' ? 'bg-blue-500' : 'bg-gray-400';
                        return (
                          <tr
                            key={campaign.id}
                            className="h-12 border-b border-[#F3F4F6] transition-colors hover:bg-[#F8F9FB]"
                            data-testid={`campaign-${campaign.id}`}
                          >
                            <td className="px-4 py-2 font-medium text-[#111827]">{campaign.name}</td>
                            <td className="px-4 py-2 text-[#6B7280]">{campaign.category}</td>
                            <td className="px-4 py-2">
                              <span className="inline-flex items-center gap-2 text-sm text-[#374151]">
                                <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
                                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-2 font-mono text-[#374151]">
                              ₹{(campaign.content_budget + campaign.distribution_budget).toLocaleString('en-IN')}
                            </td>
                            <td className="px-4 py-2 font-mono text-[#374151]">
                              {campaign.estimated_outcomes?.roi ? `${(campaign.estimated_outcomes.roi * 100).toFixed(1)}%` : '-'}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                                  className="rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-[#FEE2E2] hover:text-[#DC120F]"
                                  title="View"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => handleEditCampaign(campaign)}
                                  className="rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-[#FEE2E2] hover:text-[#DC120F]"
                                  title="Edit"
                                  data-testid={`edit-campaign-${campaign.id}`}
                                >
                                  <PencilSimple size={16} />
                                </button>
                                <button
                                  onClick={() => openAssignDialog(campaign)}
                                  className="rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-[#FEE2E2] hover:text-[#DC120F]"
                                  title="Assign Publisher"
                                >
                                  <UserPlus size={16} />
                                </button>
                                {campaign.pending_applicants > 0 && (
                                  <button
                                    onClick={() => openApplicantsDialog(campaign)}
                                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50"
                                    data-testid={`applicants-btn-${campaign.id}`}
                                    title="Applicants"
                                  >
                                    <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white">
                                      {campaign.pending_applicants}
                                    </span>
                                  </button>
                                )}
                                {campaign.status === 'draft' && (
                                  <button
                                    onClick={() => handleUpdateStatus(campaign.id, 'active')}
                                    className="rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                                    title="Activate"
                                  >
                                    <Play size={16} />
                                  </button>
                                )}
                                {campaign.status === 'active' && (
                                  <button
                                    onClick={() => handleUpdateStatus(campaign.id, 'paused')}
                                    className="rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-[#FEE2E2] hover:text-[#DC120F]"
                                    title="Pause"
                                  >
                                    <Pause size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Applicants Dialog */}
        <Dialog open={showApplicantsDialog} onOpenChange={setShowApplicantsDialog}>
          <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Campaign Applicants</DialogTitle>
            </DialogHeader>
            <div className="mb-4 rounded-lg bg-muted p-3">
              <p className="text-sm font-semibold">{selectedCampaign?.name}</p>
            </div>
            {applicants.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">No applications yet</p>
            ) : (
              <div className="space-y-3" data-testid="applicants-list">
                {applicants.map((app) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                    data-testid={`applicant-${app.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{app.publisher_name}</p>
                      <p className="text-xs text-muted-foreground">{app.publisher_email}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Applied {new Date(app.applied_at).toLocaleDateString()}
                      </p>
                    </div>
                    {app.status === 'pending' ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApproveApplicant(app.id)}
                          className="bg-emerald-600 text-white hover:bg-emerald-700 h-8 text-xs"
                          data-testid={`approve-btn-${app.id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectApplicant(app.id)}
                          className="h-8 text-xs"
                          data-testid={`reject-btn-${app.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : (
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                        app.status === 'approved'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {app.status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
